import { useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { ResizableTitle } from '../components/ResizableTitle';

const DEFAULT_COLUMN_WIDTH = 150;

/**
 * 让 antd Table 的列头宽度可用鼠标拖拽调整。
 * 用法：const { columns, components } = useResizableColumns(rawColumns);
 * 再把 columns / components 传给 <Table columns={columns} components={components} />。
 * 已拖拽调整过的宽度按列的 key/dataIndex 记录在组件内部状态中，不随每次渲染的列定义重建而丢失。
 */
export function useResizableColumns<T extends object>(baseColumns: ColumnsType<T>) {
  const [widths, setWidths] = useState<Record<string, number>>({});

  const columns = useMemo<ColumnsType<T>>(
    () =>
      baseColumns.map((col, index) => {
        const key = String((col as { key?: string }).key ?? (col as { dataIndex?: unknown }).dataIndex ?? index);
        const width = widths[key] ?? (col as { width?: number }).width ?? DEFAULT_COLUMN_WIDTH;
        return {
          ...col,
          width,
          onHeaderCell: () => ({
            width,
            onResize: (_: unknown, { size }: { size: { width: number } }) => {
              setWidths((prev) => ({ ...prev, [key]: Math.max(60, Math.round(size.width)) }));
            },
          }),
        };
      }),
    [baseColumns, widths],
  );

  const components = useMemo(() => ({ header: { cell: ResizableTitle } }), []);

  return { columns, components };
}
